import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { MemoryMonitorService } from '../../../services/memory-monitor.service';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-memory-display',
  imports: [NgClass],
  template: `
    @if (enhancedMemory(); as memory) {
      <div class="memory-display">
        <div class="memory-header">
          <span class="memory-title">Memory Usage</span>
          <span class="memory-status" [ngClass]="memory.statusClass">{{
            memory.status
          }}</span>
        </div>
        <div class="memory-main">
          <span class="memory-value">{{ memory.usedMB }}MB</span>
          <span class="memory-percentage">({{ memory.usagePercentage }}%)</span>
        </div>
        <div class="memory-bar">
          <div
            class="memory-fill"
            [style.width.%]="memory.usagePercentage"
            [ngClass]="memory.barClass"
          ></div>
        </div>
        <div class="memory-details">
          <div class="memory-detail">
            <span>Allocated:</span>
            <span>{{ memory.totalMB }}MB</span>
          </div>
          <div class="memory-detail">
            <span>Limit:</span>
            <span>{{ memory.limitMB }}MB</span>
          </div>
        </div>
      </div>
    }
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .memory-display {
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 12px;
        border-radius: 8px;
        font-size: 11px;
        z-index: 9999;
        min-width: 240px;
        backdrop-filter: blur(4px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        margin-top: 20px;
      }

      .memory-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
      }

      .memory-title {
        font-weight: bold;
        font-size: 12px;
      }

      .memory-status {
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: bold;
      }

      .status-excellent {
        background: #2e7d32;
      }
      .status-good {
        background: #388e3c;
      }
      .status-moderate {
        background: #f57c00;
      }
      .status-high {
        background: #d32f2f;
      }
      .status-critical {
        background: #b71c1c;
      }

      .memory-main {
        display: flex;
        align-items: baseline;
        gap: 6px;
        margin-bottom: 8px;
      }

      .memory-value {
        font-size: 16px;
        font-weight: bold;
        color: #ffffff;
      }

      .memory-percentage {
        font-size: 11px;
        color: #cccccc;
      }

      .memory-bar {
        width: 100%;
        height: 8px;
        background: #333;
        border-radius: 4px;
        overflow: hidden;
        margin-bottom: 10px;
      }

      .memory-fill {
        height: 100%;
        transition: all 0.3s ease;
        border-radius: 4px;
      }

      .bar-excellent {
        background: linear-gradient(90deg, #4caf50, #66bb6a);
      }
      .bar-good {
        background: linear-gradient(90deg, #8bc34a, #9ccc65);
      }
      .bar-moderate {
        background: linear-gradient(90deg, #ff9800, #ffb74d);
      }
      .bar-high {
        background: linear-gradient(90deg, #ff5722, #ff8a65);
      }
      .bar-critical {
        background: linear-gradient(90deg, #f44336, #ef5350);
      }

      .memory-details {
        display: flex;
        flex-direction: column;
        gap: 3px;
      }

      .memory-detail {
        display: flex;
        justify-content: space-between;
        color: #cccccc;
        font-size: 10px;
      }

      .memory-detail span:last-child {
        color: #ffffff;
        font-weight: 500;
      }
    `,
  ],
})
export class MemoryDisplayComponent {
  private memoryService = inject(MemoryMonitorService);

  enhancedMemory = computed(() => {
    const memory = this.memoryService.memory();
    if (!memory) return null;

    const usagePercentage =
      Math.round(
        (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100 * 100,
      ) / 100;

    let status: string;
    let statusClass: string;
    let barClass: string;

    if (memory.usedMB < 100) {
      status = 'EXCELLENT';
      statusClass = 'status-excellent';
      barClass = 'bar-excellent';
    } else if (memory.usedMB < 300) {
      status = 'GOOD';
      statusClass = 'status-good';
      barClass = 'bar-good';
    } else if (memory.usedMB < 800) {
      status = 'MODERATE';
      statusClass = 'status-moderate';
      barClass = 'bar-moderate';
    } else if (memory.usedMB < 1500) {
      status = 'HIGH';
      statusClass = 'status-high';
      barClass = 'bar-high';
    } else {
      status = 'CRITICAL';
      statusClass = 'status-critical';
      barClass = 'bar-critical';
    }

    return {
      ...memory,
      usagePercentage,
      status,
      statusClass,
      barClass,
    };
  });
}
